import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import {
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { HeaderSort } from "../../../components/TableHeaderSort";
import {
  BookingStatus,
  BookingStatusDisplayValue,
  BookingTypes,
} from "../../../helpers/enums";
import { formatDateString, isNotEmpty } from "../../../helpers/general";
import { useCookie } from "../../../hooks/useCookie";
import { useQueryParam } from "../../../hooks/useQueryParam";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";

// TODO: [MultiPlaceAdmin] in place, offers QR code page and main qr code with not visite reference
// TODO: [PlaceManager] when booking for another user, say if the user has paid or will pay in place

interface TableBodyProps {
  booking: Booking;
  showDetails: (booking: Booking) => void;
  mode: "actions" | "list";
}

function getBookingStatus(booking: Booking) {
  if (booking.status?.label?.toLowerCase() === "confirmed" && booking.isUsed) {
    return BookingStatus.PASSED;
  } else if (booking.status?.label?.toLowerCase() === "canceled") {
    return BookingStatus.CANCELED;
  } else if (booking.status?.label?.toLowerCase() === "confirmed") {
    return BookingStatus.CONFIRMED;
  } else if (booking.status?.label?.toLowerCase() === "refused") {
    return BookingStatus.REFUSED;
  } else if (booking.status?.label?.toLowerCase() === "cash_pre_payment") {
    return BookingStatus.CASH_PRE_PAYMENT;
  } else if (booking.status?.label?.toLowerCase() === "pre_payment") {
    return BookingStatus.PRE_PAYMENT;
  }
  return BookingStatus.WAITING;
}

function getBadgeClassNameByBookingStatus(booking: Booking): string {
  const bookingStatus = getBookingStatus(booking);

  switch (bookingStatus) {
    case BookingStatus.CONFIRMED:
      return "bg-success border-success rounded-pill p-2 text-white text-center";
    case BookingStatus.CASH_PRE_PAYMENT:
      return "bg-warning border rounded-pill p-2  text-center text-dark";

    case BookingStatus.PRE_PAYMENT:
      return "bg-warning border rounded-pill p-2  text-center text-dark";
    case BookingStatus.CANCELED:
      return "bg-red text-white border-success rounded-pill p-2  text-center ";
    case BookingStatus.REFUSED:
      return " bg-danger border rounded-pill p-2 text-white text-center";
    case BookingStatus.PASSED:
      return "bg-primary border-success rounded-pill p-2 text-white text-center";
    case BookingStatus.WAITING:
      return "bg-warning border-success rounded-pill p-2 text-dark text-center";
    default:
      return "bg-warning text-dark";
  }
}

function getBookingStatusDisplayValue(booking: Booking) {
  if (booking.status?.label?.toLowerCase() === "confirmed" && booking.isUsed) {
    return BookingStatusDisplayValue.PASSED;
  } else if (booking.status?.label?.toLowerCase() === "canceled") {
    return BookingStatusDisplayValue.CANCELED;
  } else if (booking.status?.label?.toLowerCase() === "confirmed") {
    return BookingStatusDisplayValue.CONFIRMED;
  } else if (booking.status?.label?.toLowerCase() === "refused") {
    return BookingStatusDisplayValue.REFUSED;
  } else if (booking.status?.label?.toLowerCase() === "cash_pre_payment") {
    return BookingStatusDisplayValue.CASH_PRE_PAYMENT;
  }
  return BookingStatusDisplayValue.WAITING;
}

function TableBodyRowContent({ booking, showDetails, mode }: TableBodyProps) {
  // const [isChecked, setIsChecked] = useState(false);
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();

  const { toastSuccess, toastError } = useToast();

  const AcceptBooking = useMutation((id: string) => {
    return apiService.MakePutRequest(`booking/${id}/approve`, {}, token);
  });

  const AcceptBookingButton = () => {
    AcceptBooking.mutate(booking.id, {
      onError(error) {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Réservation acceptée");
        queryClient.invalidateQueries(["getAllBookings"]);
      },
    });
  };

  const RefuseBooking = useMutation((id: string) => {
    return apiService.MakePutRequest(`booking/${id}/refuse`, {}, token);
  });

  const RefuseBookingButton = () => {
    RefuseBooking.mutate(booking.id, {
      onError(error) {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Réservation refusée");
        queryClient.invalidateQueries(["getAllBookings"]);
      },
    });
  };

  const getUserDisplayValue = (user: User) => {
    if (isNotEmpty(user?.fullName)) {
      return user?.fullName;
    } else if (isNotEmpty(user?.email)) {
      return user?.email;
    }
    return user?.phone;
  };

  return (
    <CTableRow
      style={{
        // borderLeft: isChecked ? `3px solid ${colors.primary}` : "",
        width: "100%",
        position: "relative",
      }}
    >
      <CTableDataCell className="p-3 text-capitalize text-sm">
        {formatDateString(booking?.date!)} - {booking?.hour}
      </CTableDataCell>

      <CTableDataCell className="p-3 text-sm">
        {booking.isForAnotherUser === true ? (
          <div className="text-sm font-bold">Partenaire :</div>
        ) : null}
        <div>{getUserDisplayValue(booking.user)}</div>
      </CTableDataCell>

      <CTableDataCell className="p-3 text-sm">
        {booking?.reference}
      </CTableDataCell>

      <CTableDataCell className={` p-3 text-capitalize text-sm`}>
        <div className={`${getBadgeClassNameByBookingStatus(booking)}`}>
          {getBookingStatusDisplayValue(booking)}
        </div>
      </CTableDataCell>
      <CTableDataCell className="p-3 text-capitalize text-sm">
        {booking?.wafacashCode !== null
          ? "Wafacash"
          : booking?.paidInApp
            ? "Carte bancaire"
            : booking?.isGuestList
              ? "Invité gueslist"
              : "Sur place"}
      </CTableDataCell>

      <CTableDataCell className="p-3 text-sm">
        {booking?.priceToPay} DH
      </CTableDataCell>

      <CTableDataCell className="p-3 text-sm">
        {booking?.isForAnotherUser === true
          ? `(${booking.placeCommission ?? 0}%)`
          : ""}{" "}
        {booking?.isForAnotherUser === true
          ? `${(
              ((booking?.priceToPay ?? 0) * (booking?.placeCommission ?? 0)) /
              100
            ).toFixed(2)} DH`
          : ""}
      </CTableDataCell>

      <CTableDataCell className="p-3 text-sm">
        {booking.isForAnotherUser === true
          ? booking?.bookingCommissionPaid?.isMultiPlaceAdminPaid
            ? "✅"
            : "❌"
          : ""}
      </CTableDataCell>

      <CTableDataCell className="p-3 text-sm">
        <div className="p-3 text-capitalize text-sm d-flex align-items-center gap-3">
          {getBookingStatus(booking) === BookingStatus.WAITING && (
            <>
              <button
                className="btn btn-primary shadow-secondary rounded-pill text-sm text-white"
                onClick={AcceptBookingButton}
                disabled={AcceptBooking.isLoading}
              >
                {AcceptBooking.isLoading ? <CSpinner size="sm" /> : " Accepter"}
              </button>
              <button
                className="btn btn-danger shadow-primary rounded-pill text-sm text-white"
                onClick={RefuseBookingButton}
                disabled={RefuseBooking.isLoading}
              >
                {RefuseBooking.isLoading ? <CSpinner size="sm" /> : " Refuser"}
              </button>
            </>
          )}
          <button
            onClick={() => showDetails(booking)}
            className="btn rounded-pill border py-1 px-3 text-sm"
          >
            Vue
          </button>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

interface TableProps {
  bookings: Booking[];
  mode?: "actions" | "list";
}
export default function BookingTable({
  bookings,
  mode = "actions",
}: TableProps) {
  const [searchParams] = useSearchParams();
  const { add } = useQueryParam();
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sort, setSort] = useState({
    field: "createdAt",
    order: "desc",
  });

  const resetSort = () => setSort({ field: "createdAt", order: "desc" });

  useEffect(() => {
    if (searchParams.get("sort")) {
      const [field, order] = searchParams.get("sort")!.split(":");
      setSort({ field, order });
    } else {
      resetSort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    add("sort", `${sort.field}:${sort.order}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  return (
    <div className="mt-3">
      <CTable responsive>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Date/Heure de réservation
                <HeaderSort
                  field="date"
                  sort={sort.order}
                  sortedField={sort}
                  setSort={(field, order) => setSort({ field, order })}
                  clearSort={() => resetSort()}
                  defaultField="createdAt"
                />
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Utilisateur{" "}
                <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Référence <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Statut <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Méthode de paiement
                <HeaderSort
                  field="paidInApp"
                  sort={sort.order}
                  sortedField={sort}
                  setSort={(field, order) => setSort({ field, order })}
                  clearSort={() => resetSort()}
                  defaultField="createdAt"
                />
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Prix total <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Commission hôtelière
                <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center gap-2">
                Commission payée
                <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {bookings?.map((booking) => (
            <TableBodyRowContent
              key={booking.id}
              booking={booking}
              showDetails={(booking: Booking) => setSelectedBooking(booking)}
              mode={mode}
            />
          ))}
        </CTableBody>
      </CTable>
      {/* // * Booking details drawer */}
      <COffcanvas
        backdrop={true}
        placement="start"
        visible={Object.keys(selectedBooking ?? {}).length > 0}
        onHide={() => setSelectedBooking(null)}
      >
        <div className="border-bottom pb-1 px-3">
          <COffcanvasHeader className="mb-3">
            <COffcanvasTitle>Détails des réservations</COffcanvasTitle>
            <CCloseButton
              className="text-reset"
              onClick={() => setSelectedBooking(null)}
            />
          </COffcanvasHeader>
          <h5>Utilisateur</h5>
          <div className="mb-1">
            <UserCircleIcon width={20} />{" "}
            <span className="text-sm">
              {selectedBooking?.isForAnotherUser === true
                ? selectedBooking?.bookedForUser?.fullName
                : selectedBooking?.user.fullName}
            </span>
          </div>
          <div className="mb-1">
            <PhoneIcon width={15} />{" "}
            <span className="text-sm">
              {selectedBooking?.isForAnotherUser === true
                ? selectedBooking?.bookedForUser?.phoneNumber
                : selectedBooking?.user.phone}
            </span>
          </div>
          <div className="mb-1">
            <EnvelopeIcon width={15} />{" "}
            <span className="text-sm">
              {selectedBooking?.isForAnotherUser === true
                ? selectedBooking?.bookedForUser?.email
                : selectedBooking?.user.email}
            </span>
          </div>
        </div>
        {selectedBooking?.isForAnotherUser === true && (
          <div className="border-bottom py-3 px-3">
            <h5>Partenaire</h5>
            <div className="mb-1">
              <span className="text-sm">{selectedBooking?.user.fullName}</span>
            </div>
          </div>
        )}
        <COffcanvasBody>
          <CRow className="align-items-center justify-content-between">
            <CCol sm="auto">
              {selectedBooking?.place?.name ?? selectedBooking?.event?.name}
            </CCol>
            <CCol sm="auto">
              <button className="btn btn-danger rounded-full py-0 px-2 text-white">
                {selectedBooking &&
                  getBookingStatusDisplayValue(selectedBooking)}
              </button>
            </CCol>
          </CRow>

          <CRow className="align-items-center justify-content-between mt-3">
            <CCol sm="auto" title="Reference">
              <p className="text-sm mb-0">Reference</p>
              {selectedBooking?.reference}
            </CCol>
          </CRow>

          {selectedBooking?.type === BookingTypes.PLACE_BOOKING ||
          (selectedBooking?.type === BookingTypes.SPECIAL_OFFER_BOOKING &&
            selectedBooking?.variantsBookings!?.length === 0) ? (
            <CRow className="bg-primary-shade p-2 rounded-sm align-items-center justify-content-between mt-3">
              <CCol sm="auto" title="Reference">
                Nombre de personnes: {selectedBooking?.personsNumber}
              </CCol>
            </CRow>
          ) : null}

          <CRow className="align-items-center justify-content-between mt-3">
            <CCol sm="auto" title="Reference">
              <span className="font-bold">Méthode de paiement: </span>
              {selectedBooking?.wafacashCode !== null
                ? "Wafacash"
                : selectedBooking?.paymentMethod === "credit_card" ||
                    selectedBooking?.paymentMethod === "stripe"
                  ? "Carte bancaire"
                  : selectedBooking?.isGuestList
                    ? "Invité guestlist"
                    : "Sur place"}
            </CCol>
          </CRow>

          {selectedBooking?.packsBookings!?.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 border-bottom fw-bold">Pack réservé</p>
              <CAccordion flush>
                {selectedBooking?.packsBookings.map((pack) => (
                  <CAccordionItem key={pack.id}>
                    <CAccordionHeader className="align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center gap-2 w-75">
                        <img
                          src={pack.pack.image}
                          alt=""
                          width={40}
                          height={40}
                          className="rounded-sm"
                        />

                        <p className="mb-0">
                          {pack.numberOfReservation} x {pack.pack.title}{" "}
                        </p>
                      </div>
                      {selectedBooking.priceToPayCurrency === "euro" ? (
                        <p className="mb-0">
                          {/* @ts-ignore */}
                          {pack.pack.priceInEuro * pack.numberOfReservation}€
                        </p>
                      ) : (
                        <p className="mb-0">
                          {/* @ts-ignore */}
                          {pack.pack.price * pack.numberOfReservation}
                          DH
                        </p>
                      )}
                    </CAccordionHeader>
                    <CAccordionBody>
                      <ul>
                        {pack.pack.items.map((packItem) => (
                          <li key={packItem.id} className="mb-1">
                            {packItem.title}
                          </li>
                        ))}
                      </ul>
                    </CAccordionBody>
                  </CAccordionItem>
                ))}
              </CAccordion>
            </div>
          )}
          {selectedBooking?.type === BookingTypes.SPECIAL_OFFER_BOOKING && (
            <div className="mt-4">
              <span className="font-bold">Offre: </span>
              {selectedBooking?.special?.title}
            </div>
          )}
          {selectedBooking?.variantsBookings!?.length > 0 && (
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">Variant reservé</p>
              {selectedBooking?.variantsBookings.map((item) => (
                <CRow
                  key={item.id}
                  className="mb-2 align-items-center justify-content-between"
                >
                  <CCol sm="auto">
                    {item.numberOfReservation} x {item.variant.description}
                  </CCol>
                  {selectedBooking.priceToPayCurrency === "euro" ? (
                    <CCol sm="auto">
                      {item.numberOfReservation *
                        parseFloat(item.variant.priceInEuro)}{" "}
                      €
                    </CCol>
                  ) : (
                    <CCol sm="auto">
                      {item.numberOfReservation *
                        parseFloat(item.variant.price)}{" "}
                      DH
                    </CCol>
                  )}
                </CRow>
              ))}
            </div>
          )}

          {selectedBooking?.servicesBookings!?.length > 0 && (
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">Service réservé</p>
              {selectedBooking?.servicesBookings.map((item) => (
                <CRow
                  key={item.id}
                  className="mb-2 align-items-center justify-content-between"
                >
                  <CCol sm="auto">
                    {item.personsNumber} x {item.service.label}
                  </CCol>
                  {selectedBooking.priceToPayCurrency === "euro" ? (
                    <CCol sm="auto">
                      {item.personsNumber * item.service.startingPriceEuro} €
                    </CCol>
                  ) : (
                    <CCol sm="auto">
                      {item.personsNumber * item.service.startingPrice} DH
                    </CCol>
                  )}
                </CRow>
              ))}
              {selectedBooking?.appliedPromoCodes &&
                selectedBooking?.appliedPromoCodes?.length > 0 && (
                  <>
                    <CRow className=" mt-3 pt-2 mb-2 align-items-center justify-content-between border-top">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Sous-Total:
                        </strong>
                      </CCol>
                      {selectedBooking.priceToPayCurrency === "euro" ? (
                        <CCol sm="auto">
                          {selectedBooking?.servicesBookings?.reduce(
                            (total, service) => {
                              return (
                                total +
                                service.service.startingPriceEuro *
                                  service.personsNumber
                              );
                            },
                            0,
                          )}{" "}
                          €
                        </CCol>
                      ) : (
                        <CCol sm="auto">
                          {selectedBooking?.servicesBookings?.reduce(
                            (total, service) => {
                              return (
                                total +
                                service.service.startingPrice *
                                  service.personsNumber
                              );
                            },
                            0,
                          )}{" "}
                          DH
                        </CCol>
                      )}
                    </CRow>
                    {selectedBooking && (
                      <CRow className="mb-2 align-items-center justify-content-between">
                        <CCol sm="auto">
                          <strong style={{ fontSize: "14px" }}>
                            {" "}
                            Remise (
                            {selectedBooking?.appliedPromoCodes[0].discount}
                            %):
                          </strong>
                        </CCol>
                        {selectedBooking.priceToPayCurrency === "euro" ? (
                          <CCol sm="auto">
                            -
                            {selectedBooking?.servicesBookings.reduce(
                              (total, service) =>
                                total +
                                service.service.startingPriceEuro *
                                  service.personsNumber,
                              0,
                            ) - selectedBooking?.priceToPay}{" "}
                            €
                          </CCol>
                        ) : (
                          <CCol sm="auto">
                            -
                            {selectedBooking?.servicesBookings.reduce(
                              (total, service) =>
                                total +
                                service.service.startingPrice *
                                  service.personsNumber,
                              0,
                            ) - selectedBooking?.priceToPay}{" "}
                            DH
                          </CCol>
                        )}
                      </CRow>
                    )}
                  </>
                )}
              <CRow className="mb-2 align-items-center justify-content-between">
                <CCol sm="auto">
                  {" "}
                  <strong style={{ fontSize: "14px" }}>Total:</strong>{" "}
                </CCol>
                <CCol sm="auto">
                  <strong>
                    {selectedBooking?.priceToPay ??
                      selectedBooking?.servicesBookings?.reduce(
                        (total, service) => {
                          return (
                            total +
                            service.service.startingPrice *
                              service.personsNumber
                          );
                        },
                        0,
                      )}{" "}
                    {selectedBooking?.priceToPayCurrency === "euro"
                      ? "€"
                      : "DH"}
                  </strong>{" "}
                </CCol>
              </CRow>
            </div>
          )}

          <div className="mt-2">
            <div className="d-flex">
              <div className="me-2 font-semibold">Prix total:</div>
              <div>{selectedBooking?.priceToPay} DH</div>
            </div>
            <div className="d-flex">
              <div className="me-2 font-semibold">
                Commission hôtelière ({selectedBooking?.placeCommission ?? 0}%):
              </div>
              <div>
                {(
                  ((selectedBooking?.priceToPay ?? 0) *
                    (selectedBooking?.placeCommission ?? 0)) /
                  100
                ).toFixed(2)}{" "}
                DH
              </div>
            </div>
            <div className="d-flex">
              <div className="me-2 font-semibold">Commission payée:</div>
              <div>
                {selectedBooking?.bookingCommissionPaid?.isMultiPlaceAdminPaid
                  ? "✅"
                  : "❌"}
              </div>
            </div>
          </div>

          {selectedBooking?.createdAt && (
            <div className="border-top p-1 pt-2 mt-3">
              <p className="mb-0">
                Date de création :{" "}
                {formatDateString(selectedBooking?.createdAt)}
              </p>
              <p className="mb-0">
                Heure de création :{" "}
                {format(new Date(selectedBooking?.createdAt), "HH:mm")}
              </p>
            </div>
          )}

          {selectedBooking?.date && (
            <div className="border-top p-1 pt-2 mt-3">
              <p className="mb-0">
                Date de réservation : {formatDateString(selectedBooking?.date)}
              </p>
              <p className="mb-0">
                Heure de réservation : {selectedBooking?.hour}
              </p>
            </div>
          )}

          {selectedBooking?.scanedBy && (
            <div className="border-top p-1 mt-3">
              <p className="mb-0">
                Scanné par: {selectedBooking?.scanedBy?.fullName}{" "}
              </p>
            </div>
          )}
          {selectedBooking?.scanedAt && (
            <div className="border-bottom p-1">
              <p className="mb-0">
                Scanné à:{" "}
                {format(
                  new Date(selectedBooking?.scanedAt),
                  "dd/MM/yyyy - HH:mm",
                )}
              </p>
            </div>
          )}

          {selectedBooking?.bookingFor?.name && (
            <div className="border-top p-1 mt-3">
              <h6 className="mb-0">Réservé pour :</h6>
              <p className="mb-0">
                Nom de la personne: {selectedBooking?.bookingFor?.name}
              </p>
              <p className="mb-0">
                Email: {selectedBooking?.bookingFor?.email}
              </p>
              <p className="mb-0">
                Téléphone: {selectedBooking?.bookingFor?.phone}
              </p>
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </div>
  );
}
